import { AppDispatch, RootState } from "@Reducers/index";
import type {
  EstimateFieldsNeedToRefresh,
  TakeoffData as TakeoffDataHelper,
} from "@Helpers/types/estimation/takeoffTypes";
import { getBackendUrl } from "@Utils";
import type { Exact } from "@Helpers/types/exact";
import type {
  TakeoffData,
  AddItemsToTakeoffsRequest,
  AddItemsToTakeoffsResponse,
  CreateTakeoffRequest,
  CreateTakeoffResponse,
  DuplicateItemInTakeoffRequest,
  DuplicateItemInTakeoffResponse,
  DuplicateTakeoffRequest,
  DuplicateTakeoffResponse,
  GetTakeoffRequest,
  GetTakeoffResponse,
  GetTakeoffsListRequest,
  GetTakeoffsListResponse,
  GetTakeoffsListAttachedToItemRequest,
  GetTakeoffsListAttachedToItemResponse,
  GetTakeoffWizardRequest,
  GetTakeoffWizardResponse,
  GetTakeoffWizardMeasurementsRequest,
  GetTakeoffWizardMeasurementsResponse,
  RemoveItemsFromTakeoffsRequest,
  RemoveItemsFromTakeoffsResponse,
  RemoveTakeOffAttachedQueryRequest,
  RemoveTakeOffAttachedQueryResponse,
  UpgradeTakeOffAttachedQueryRequest,
  UpgradeTakeOffAttachedQueryResponse,
} from "@ServerTypes/takeoffs/takeoffs.schema";
import { estimateMeasurementsUrl, getValidItemsUrl, portalMeasurementUnitsUrl } from "@Services/dataManager";
import type {
  MeasurementItem,
  AddMeasurementsToTakeoffsRequest,
  AddMeasurementsToTakeoffsResponse,
  CreateMeasurementRequest,
  CreateMeasurementResponse,
  DuplicateMeasurementRequest,
  DuplicateMeasurementResponse,
  GetMeasurementsRequest,
  GetMeasurementsResponse,
  RemoveMeasurementsFromTakeoffsRequest,
  RemoveMeasurementsFromTakeoffsResponse,
  UpdateMeasurementTagsRequest,
  UpdateMeasurementTagsResponse,
  AddMeasurementsToTakeoffsFromNodesRequest,
  AddMeasurementsToTakeoffsFromNodesResponse,
} from "@ServerTypes/measurements/measurements.schema";
import axiosWrapper from "../services/axiosWrapper";
import { userSettingsType } from "../services/constants";
import _ from "lodash";
import { getIsViewerDependencyLoaded, getNumberBasedOnSettings } from "@Services/utils/helpers";
import { CubeGlobal } from "@Features/viewer-core";
import { t } from "@i18n";
import type {
  AddFilesToEstimateRequest,
  AddFilesToEstimateResponse,
  ChangeDisplayUnitRequest,
  ChangeDisplayUnitResponse,
  ChangeItemTypeInEstimateRequest,
  ChangeItemTypeInEstimateResponse,
  CheckIfDsrVersionIsLatestRequest,
  CheckIfDsrVersionIsLatestResponse,
  CreateEstimateVersionRequest,
  CreateEstimateVersionResponse,
  GetAdditionalCostRequest,
  GetAdditionalCostResponse,
  DeleteAdditionalCostRequest,
  GetEstimateAdditionalCostTotalRequest,
  GetEstimateAdditionalCostTotalResponse,
  GetEstimateFilesResponse,
  RemoveFileFromEstimateRequest,
  GetEstimateItemRequest,
  GetEstimateItemResponse,
  GetEstimateItemsRequest,
  GetEstimateItemsResponse,
  UpdateEstimateItemRequest,
  SaveItemInEstimatePayload,
  GetEstimateItemsRemoteRequest,
  GetEstimateItemsRemoteResponse,
  EstimateListInsertValue,
  GetEstimateListResponse,
  CreateEstimateRequest,
  UpdateEstimateDataRequest,
  DeleteEstimateRequest,
  GetEstimateTotalRequest,
  GetEstimateTotalResponse,
  GetEstimateVersionRequest,
  GetEstimateVersionResponse,
  GetValidItemsRequest,
  GetValidItemsResponse,
  EstimateData,
  EstimateListItem,
  RestoreEstimateVersionRequest,
  RestoreEstimateVersionResponse,
  SaveAdditionalCostRequest,
  SaveAdditionalCostResponse,
  SaveEstimateItemsFromExcelSheetRequest,
  SaveEstimateItemsFromExcelSheetResponse,
  SaveEstimateSettingsParams as saveEstimateSettingsParams,
  SaveItemDetailsRequest,
  SaveItemDetailsResponse,
  UpdateCostDatabaseInEstimateRequest,
  UpdateCostDatabaseInEstimateResponse,
  UpdatedEstimateItemSerialRequest,
  UpdatedEstimateItemSerialResponse,
} from "@ServerTypes/estimates/estimates.schema.js";
import type {
  CanUpdateMeasurementUnitTypeRequest,
  CanUpdateMeasurementUnitTypeResponse,
  SaveItemMeasurementUnitRequest,
  SaveItemMeasurementUnitResponse,
  SyncItemMeasurementUnitRequest,
  SyncItemMeasurementUnitResponse,
  GetPurchaseUnitsRequest,
  GetPurchaseUnitsResponse,
  GetMeasurementUnitsRequest,
  GetMeasurementUnitsResponse,
} from "@ServerTypes/units/units.schema.js";
import type {
  DiscardDraftVersionRequest,
  DiscardDraftVersionResponse,
  GetAllDsrItemsRequest,
  GetAllDsrItemsResponse,
  InsertDsrItemRequest,
  UpdateDsrItemRequest,
  DeleteDsrItemRequest,
  GetAllVersionsOfDsrRequest,
  GetAllVersionsOfDsrResponse,
  GetDsrDataRequest,
  GetDsrDataResponse,
  GetDsrItemRequest,
  GetDsrItemResponse,
  PublishCostDatabaseVersionRequest,
  PublishCostDatabaseVersionResponse,
  SaveCostDatabaseItemsFromExcelSheetRequest,
  SaveCostDatabaseItemsFromExcelSheetResponse,
  SaveItemVariableRateRequest,
  SaveItemVariableRateResponse,
} from "@ServerTypes/dsr/dsr.schema.js";
import type {
  SaveUserSettingsRequest,
  SaveUserSettingsResponse,
} from "@ServerTypes/userSettings/userSettings.schema.js";
import type { StatusResponse } from "@Helpers/schemas/common.schema.js";
import type {
  QueriesDataRequest,
  QueriesDataResponse,
  SaveCurrentColumnSequenceRequest,
  SaveCurrentColumnSequenceResponse,
  GetQuerySavedColumnsRequest,
  GetQuerySavedColumnsResponse,
} from "@ServerTypes/queries/queries.schema.js";

export interface UpdateEstimateDataArgs {
  estimateId: string;
  name?: string;
  description?: string | null;
  costIndex?: number;
}

export type ReferenceMeasurement = {
  id: string;
  name: string;
  measurement: number;
  variable?: string;
  serial: number;
  quantity?: number;
  multiplier?: number;
  unit?: { factor: number };
};

export type ManualMeasurementState = {
  name: string;
  type: string;
  unit: string;
  value: number | null;
  references: ReferenceMeasurement[];
  isReferenced: boolean;
  formula: string;
  takeOffs?: TakeoffDataHelper[];
  measurementId?: string;
};

export type ReferenceAdditionalCost = {
  id: string;
  name: string;
  value: number;
  variable?: string;
  serial: number;
};

export type ManualAdditionalCostState = {
  name: string;
  value: number | null;
  references: ReferenceAdditionalCost[];
  isReferenced: boolean;
  formula: string;
  id?: string;
};

export type CentralManualCostState = ManualAdditionalCostState | ManualMeasurementState;
export type CentralReferenceList = ReferenceAdditionalCost | ReferenceMeasurement;

export const DSR_LEVEL_MEASUREMENT_UNIT = "dsr_level_unit";
export const ESTIMATE_LEVEL_MEASUREMENT_UNIT = "estimate_level_unit";

export const estimateGeneralSettingFields = {
  PREFERENCES: {
    field: "general",
    text: t("General"),
  },
  NUMBERING_SYSTEM: {
    field: "numberingSystem",
    text: t("Numbering System"),
  },
  ROUNDING_PREFERENCES: {
    field: "roundingPreferences",
    text: t("Rounding Preferences"),
  },
  UNITS: {
    field: "Units",
    text: t("Units"),
  },
} as const;
export type EstimateGeneralFieldKey = keyof typeof estimateGeneralSettingFields;

export const getEditBOQItemsTabs = () => ({
  DETAILS: { text: t("Details"), value: "DETAILS" },
  QUANTITY: { text: t("Quantity"), value: "QUANTITY" },
  UNIT: { text: t("Unit"), value: "UNIT" },
  RATE: { text: t("Rate"), value: "RATE" },
  AMOUNT: { text: t("Amount"), value: "AMOUNT" },
});

export const measurementSources = {
  MEASUREMENTS: "measurements",
  TAKEOFFS: "takeoffs",
  ESTIMATE: "estimate",
  TAKEOFF_SIDEBAR: "takeoff_sidebar",
  REFERENCE_MEASUREMENT: "reference",
};

export const takeoffWizardResultsErrors = {
  VALUE_MISSING: "Value Missing",
  ITEM_NOT_FOUND: "Cost Database item does not exist",
  UNIT_MISMATCH: "Unit doesn’t match with Item’s expected Measurement Unit",
  INVALID_PARAMETER: "Invalid parameter value",
  DUPLICATE_PARAMETER: "Duplicate parameters exist",
};

export const manualCostSources = {
  MEASUREMENTS: "measurements",
  ADDITIONAL_COST: "additionalCost",
};
export type ManualCostSource = (typeof manualCostSources)[keyof typeof manualCostSources];

export const numberingSystemOptions = [
  {
    item: "1, 2, 3",
    value: 0,
  },
  {
    item: "a, b, c",
    value: 1,
  },
  {
    item: "A, B, C",
    value: 2,
  },
  {
    item: "i, ii, iii",
    value: 3,
  },
  {
    item: "I, II, III",
    value: 4,
  },
];

export const takeoffSources = { VIEWER: "viewer", ESTIMATE: "estimate" };

export const itemListSources = {
  COST_DATABASE: "costDatabase",
  ESTIMATE: "estimate",
  ADD_ITEM_POPUP: "addItemToBoq",
  LINK_BOQ_ITEMS: "linkBoqItems",
  PREVIEW_BOQ_ITEMS: "previewBoqItems",
  CONTRACT_CDB: "contractCDB",
  BOQ_PRINT: "boqPrint",
  ESTIMATE_ATTACHED_CD_PREVIEW: "estimateCDPreview",
} as const;

export const unitTypesList = [
  { title: "LINEAR", value: "linear", name: "Linear" },
  { title: "AREA", value: "area", name: "Area" },
  { title: "VOLUME", value: "volume", name: "Volume" },
  { title: "COUNT", value: "count", name: "Count" },
];

export const unitTypes = {
  LINEAR: { value: "Linear", name: "Length" },
  AREA: { value: "Area", name: "Area" },
  VOLUME: { value: "Volume", name: "Volume" },
  COUNT: { value: "Count", name: "Count" },
  ANGLE: { value: "Angle", name: "Angle" },
};

export const getMeasurementDetails = (_name: string): { type: string; name: string } | { type: null; name: null } => {
  switch (_name) {
    case "QTakeOffLengthMarkup":
    case "QTakeOffLengthWithBreakMarkup":
    case "QTakeOffCircumferenceMarkup":
    case "QTakeOffLinearDropMarkup":
    case "QTakeOffSlopeLengthMarkup":
    case "QTakeOffThreePtArcLengthMarkup":
      return { type: unitTypes.LINEAR.value, name: unitTypes.LINEAR.name };

    case "QTakeOffAreaMarkup":
    case "QTakeOffSlopeAreaMarkup":
    case "SurfaceAreaDeductionMarkup":
    case "SurfaceAreaDeductionDepthMarkup":
    case "QTakeOffCircleAreaMarkup":
    case "Surface Area":
    case "QTakeOffThreePtArcAreaMarkup":
      return { type: unitTypes.AREA.value, name: unitTypes.AREA.name };

    case "TwoDVolumeMarkup":
    case "OneDVolumeMarkup":
      return { type: unitTypes.VOLUME.value, name: unitTypes.VOLUME.name };

    case "QTakeOffCountMarkup":
      return { type: unitTypes.COUNT.value, name: unitTypes.COUNT.name };

    default:
      return { type: null, name: null };
  }
};

export const countSymbols = [
  { name: "TRIANGLE", value: 0 },
  { name: "SQUARE", value: 1 },
  { name: "PENTAGON", value: 2 },
  { name: "HEXAGON", value: 3 },
  { name: "OCTAGON", value: 4 },
];

export const hexToRgb = (h: string) => {
  let r: string | number = 0;
  let g: string | number = 0;
  let b: string | number = 0;

  if (h.length === 4 || h.length === 5) {
    r = `0x${h[1]}${h[1]}`;
    g = `0x${h[2]}${h[2]}`;
    b = `0x${h[3]}${h[3]}`;
  }

  if (h.length === 7 || h.length === 9) {
    r = `0x${h[1]}${h[2]}`;
    g = `0x${h[3]}${h[4]}`;
    b = `0x${h[5]}${h[6]}`;
  }

  return { red: Number.parseInt(String(r)), green: Number.parseInt(String(g)), blue: Number.parseInt(String(b)) };
};

export const getDisplayQuantity = ({
  quantity = 0,
  factor,
  rate,
}: {
  quantity?: number;
  factor?: number;
  rate?: number;
}): { quantity: number; amount: number } => {
  const displayQuantity = factor ? quantity / factor : quantity;
  const amount = rate ? displayQuantity * rate : 0;
  return { quantity: displayQuantity, amount };
};

export const getDefaultQuantity = ({ quantity, factor }: { quantity?: number; factor?: number }) => {
  if (factor && quantity) {
    const value = String(quantity * factor);
    return Number.parseFloat(value);
  }

  return quantity;
};

export const correctDecimalPlaces = ({ value, decimals }: { value?: number | string; decimals?: number }): number => {
  if (!value) return 0;
  if (typeof decimals !== "number") decimals = 2;

  return Number.parseFloat(Number.parseFloat(String(value)).toFixed(decimals));
};

export const loadMarkupStylesViewer = ({
  viewerImpl,
  viewerMarkup,
  lineThickness,
  lineColor,
  fillColor,
  fillOpacity,
  symbolSize,
  countSymbol,
  isModelStructureReady,
  isSettingsApplied,
}: Record<string, any>) => {
  if (!viewerMarkup) return;

  const setAttribute = (setter: string, value: unknown, viewerMarkup: Record<string, any>) => {
    if (viewerMarkup[setter] && value !== undefined) {
      viewerMarkup[setter](value);
    }
  };

  setAttribute("setStrokeWidth", lineThickness, viewerMarkup);
  setAttribute("setStrokeColor", lineColor, viewerMarkup);
  setAttribute("setFillColor", fillColor, viewerMarkup);
  setAttribute("setFillOpacity", fillOpacity, viewerMarkup);
  setAttribute("setMarkupSize", symbolSize, viewerMarkup);
  setAttribute("setMarkupShape", countSymbol, viewerMarkup);

  if (getIsViewerDependencyLoaded({ viewerImpl, isModelStructureReady, isSettingsApplied, hwv: CubeGlobal.hwv })) {
    viewerImpl.hwv.markupManager.refreshMarkup(viewerImpl.hwv.view);
  }
};

export const prepareMarkupColor = (color: string) => {
  const rgbColor = hexToRgb(color);
  return new Color(rgbColor.red, rgbColor.green, rgbColor.blue);
};

import {
  setMeasurementsData,
  setTakeoffsList,
  setTakeoff,
  setMeasurementsModalStatus as setMeasurementsModalStatusReducer,
} from "../reducers/measurements";
import { setEstimateAdditionalCostTotal as setAdditionalCostTotalAction } from "../reducers/EstimateList";
import { setCostDatabase as setCostdatabaseAction } from "../reducers/DsrReducer";
import { GetEstimateDataProp } from "./EstimateList";
import { Color } from "@ts3d-hoops/web-viewer";

export const prepareEstimationFieldNeedToRefresh = ({
  estimateFieldsNeedToRefresh,
  HoopsModalStatus,
  takeoffs,
  measurements,
}: {
  estimateFieldsNeedToRefresh: EstimateFieldsNeedToRefresh;
  HoopsModalStatus: RootState["HoopsModalStatus"];
  takeoffs?: string[];
  measurements?: boolean;
}): EstimateFieldsNeedToRefresh => {
  const data = { ...estimateFieldsNeedToRefresh };
  if (_.get(HoopsModalStatus, "Takeoffs.show") && takeoffs) {
    data.takeoffs = takeoffs;
  }
  if (_.get(HoopsModalStatus, "Measurements.show") && measurements !== undefined) {
    data.measurements = measurements;
  }

  return data;
};

export const changeEstimateUnits = async <T extends Exact<ChangeDisplayUnitRequest, T>>(
  data: T,
): Promise<{ data: ChangeDisplayUnitResponse }> => {
  const response = await axiosWrapper.post<ChangeDisplayUnitResponse>(
    `${getBackendUrl()}/estimate/changeDisplayUnit`,
    data,
  );
  return response;
};

/**
 * Check if a measurement unit type can be updated.
 * Returns false if the unit is:
 * - A default/system unit
 * - Attached to takeoffs, measurements, estimates, estimate items, or cost database items
 *
 * @param params - Request parameters with measurement unit _id
 * @returns Promise with response containing { data: boolean } indicating if update is allowed
 */
export const canUpdateMeasurementUnitType = async ({
  _id,
}: CanUpdateMeasurementUnitTypeRequest): Promise<{ data: CanUpdateMeasurementUnitTypeResponse }> => {
  const response = await axiosWrapper.post<CanUpdateMeasurementUnitTypeResponse>(
    `${getBackendUrl()}/units/canUpdateMeasurementUnitType`,
    { _id },
  );
  return response;
};

export const createNewEstimate = async (value: EstimateListInsertValue) => {
  const data: CreateEstimateRequest = value;
  const response = await axiosWrapper.post<GetEstimateListResponse>(`${getBackendUrl()}/estimate/createEstimate`, data);
  return response;
};

export const deleteEstimate = async ({
  estimateId,
}: {
  estimateId: string;
}): Promise<{ data: GetEstimateListResponse }> => {
  const data: DeleteEstimateRequest = { _id: estimateId };
  const response = await axiosWrapper.post<GetEstimateListResponse>(`${getBackendUrl()}/estimate/deleteEstimate`, data);
  return response;
};

// Custom interface for saveItemMeasurementUnit that splits conversionRate into two parts
interface SaveItemMeasurementUnitParams extends Omit<SaveItemMeasurementUnitRequest, "conversionRate"> {
  conversionRatePU: string;
  conversionRateMU: string;
}

export const saveItemMeasurementUnit = async ({
  itemId,
  measurementUnitId,
  conversionRatePU,
  conversionRateMU,
  resourceLevel,
  resourceId,
}: SaveItemMeasurementUnitParams): Promise<{ data: SaveItemMeasurementUnitResponse }> => {
  const data: SaveItemMeasurementUnitRequest = {
    itemId,
    measurementUnitId,
    conversionRate: conversionRatePU.concat(":", conversionRateMU),
    resourceLevel,
    resourceId,
  };
  const response = await axiosWrapper.post<SaveItemMeasurementUnitResponse>(
    `${getBackendUrl()}/units/saveItemMeasurementUnit`,
    data,
  );
  return response;
};

export const syncItemMeasurementUnit = async ({
  itemId,
  resourceLevel,
  resourceId,
  dsrVersionId,
}: SyncItemMeasurementUnitRequest): Promise<{ data: SyncItemMeasurementUnitResponse }> => {
  const data: SyncItemMeasurementUnitRequest = {
    itemId,
    resourceLevel,
    resourceId,
    dsrVersionId,
  };
  const response = await axiosWrapper.post<SyncItemMeasurementUnitResponse>(
    `${getBackendUrl()}/units/syncItemMeasurementUnit`,
    data,
  );
  return response;
};

export const getDsrItem = async <T extends Exact<GetDsrItemRequest, T>>(
  data: T,
): Promise<{ data: GetDsrItemResponse }> => {
  const response = await axiosWrapper.post<GetDsrItemResponse>(`${getBackendUrl()}/dsr/getDsrItem`, data);
  return response;
};

export const getEstimateItem = async ({
  itemId,
  estimateId,
  versionId,
}: GetEstimateItemRequest): Promise<{ data: GetEstimateItemResponse }> => {
  const response = await axiosWrapper.post<GetEstimateItemResponse>(`${getBackendUrl()}/estimate/getEstimateItem`, {
    itemId,
    estimateId,
    versionId,
  });
  return response;
};

export const deleteItemsFromTakeoffs = async ({
  data,
  estimateId,
}: RemoveItemsFromTakeoffsRequest): Promise<{ data: RemoveItemsFromTakeoffsResponse }> => {
  const response = await axiosWrapper.post<RemoveItemsFromTakeoffsResponse>(
    `${getBackendUrl()}/takeoffs/removeItemsFromTakeoffs`,
    { data, estimateId },
  );
  return response;
};

export const addFilesToEstimate = async ({
  fileVersionIds,
  estimateId,
}: AddFilesToEstimateRequest): Promise<{ data: AddFilesToEstimateResponse }> => {
  const response = await axiosWrapper.post<AddFilesToEstimateResponse>(
    `${getBackendUrl()}/estimate/addFilesToEstimate`,
    { fileVersionIds, estimateId },
  );
  return response;
};

export const removeFilesFromEstimate = async (
  fileVersionId: string,
  estimateId: string,
): Promise<{ data: GetEstimateFilesResponse }> => {
  const data: RemoveFileFromEstimateRequest = { fileVersionId, estimateId };
  const response = await axiosWrapper.post<GetEstimateFilesResponse>(
    `${getBackendUrl()}/estimate/removeFileFromEstimate`,
    data,
  );
  return response;
};

export const getTakeoffsList = async ({
  estimateId,
  excludeMeasurements = null,
  type,
  versionId,
}: {
  estimateId: string;
  excludeMeasurements?: string[] | null;
  type?: string;
  versionId?: string;
}): Promise<{ data: GetTakeoffsListResponse }> => {
  const data: GetTakeoffsListRequest = {
    value: { params: { estimateId, excludeMeasurements: excludeMeasurements ?? undefined, versionId, type } },
  };
  const response = await axiosWrapper.post<GetTakeoffsListResponse>(
    `${getBackendUrl()}/takeoffs/getTakeoffsList`,
    data,
  );
  return response;
};

export function getTakeoffsListData({
  estimateId,
  excludeMeasurements = null,
  versionId,
  skipSystemGenerated,
}: {
  estimateId: string;
  excludeMeasurements?: string[] | null;
  versionId?: string;
  skipSystemGenerated?: boolean;
}) {
  return async (dispatch: AppDispatch) => {
    const data: GetTakeoffsListRequest = {
      value: {
        params: { estimateId, excludeMeasurements: excludeMeasurements ?? undefined, versionId, skipSystemGenerated },
      },
    };
    const response = await axiosWrapper.post<GetTakeoffsListResponse>(
      `${getBackendUrl()}/takeoffs/getTakeoffsList`,
      data,
    );
    if (!response.data.status) return;
    dispatch(setTakeoffsList(response.data.result));
    return response.data.result;
  };
}

export const getTakeoff = async ({
  takeOffItemId,
  versionId,
  estimateId,
}: GetTakeoffRequest): Promise<{ data: GetTakeoffResponse }> => {
  const response = await axiosWrapper.post<GetTakeoffResponse>(`${getBackendUrl()}/takeoffs/getTakeoff`, {
    takeOffItemId,
    versionId,
    estimateId,
  });
  return response;
};

export function getTakeoffData({ takeOffItemId, versionId, estimateId }: GetTakeoffRequest) {
  return async (dispatch: AppDispatch) => {
    const data: GetTakeoffRequest = { takeOffItemId, versionId, estimateId };
    const response = await axiosWrapper.post<GetTakeoffResponse>(`${getBackendUrl()}/takeoffs/getTakeoff`, data);
    if (!response.data.status || !response.data.result) return;

    const takeoffData = response.data.result;
    const saveData = {
      ...takeoffData,
      quantity: getDisplayQuantity({
        quantity: takeoffData.quantity,
        factor: takeoffData.measurementUnit.factor,
      }).quantity,
      additionalQuantity: getDisplayQuantity({
        quantity: takeoffData.additionalQuantity,
        factor: takeoffData.measurementUnit.factor,
      }).quantity,
    };

    dispatch(setTakeoff(saveData));
    return saveData;
  };
}

export function setTakeoffData(data: Partial<TakeoffData>) {
  return (dispatch: AppDispatch) => dispatch(setTakeoff(data));
}

export const createTakeoff = async ({
  name,
  description,
  type,
  estimateId,
}: CreateTakeoffRequest): Promise<{ data: CreateTakeoffResponse }> => {
  const data: CreateTakeoffRequest = {
    name,
    description,
    type,
    estimateId,
  };
  const response = await axiosWrapper.post<CreateTakeoffResponse>(`${getBackendUrl()}/takeoffs/createTakeoff`, data);
  return response;
};

export const getEstimateItemsList = async (
  args:
    | {
        source: typeof itemListSources.ESTIMATE | typeof itemListSources.BOQ_PRINT;
        estimateId: string;
        versionId?: string | null;
        parentId?: string | null;
        skip?: number;
        take?: number;
        getAllItems?: boolean;
      }
    | {
        source: typeof itemListSources.COST_DATABASE | typeof itemListSources.ESTIMATE_ATTACHED_CD_PREVIEW;
        dsrId: string;
        versionId?: string | null;
        parentId?: string | null;
        skip?: number;
        take?: number;
        getAllItems?: boolean;
        publishedVersion?: boolean;
      },
): Promise<{ data: GetEstimateItemsResponse } | { data: GetAllDsrItemsResponse } | undefined> => {
  const { source, parentId, skip, take } = args;
  const whereClause = parentId
    ? [
        {
          field: "parentId",
          ignoreCase: false,
          isComplex: false,
          operator: "equal",
          value: parentId,
        },
      ]
    : undefined;

  if (source === itemListSources.ESTIMATE || source === itemListSources.BOQ_PRINT) {
    const { estimateId, getAllItems, versionId } = args;
    const params: GetEstimateItemsRequest = {
      value: {
        requiresCounts: true,
        ...(whereClause ? { where: whereClause } : {}),
        ...(skip ? { skip } : {}),
        ...(take ? { take } : {}),
        params: { estimateId, getAllItems, versionId: versionId ?? undefined },
      },
    };
    return await getEstimateItems(params);
  } else if (source === itemListSources.COST_DATABASE || source === itemListSources.ESTIMATE_ATTACHED_CD_PREVIEW) {
    const { dsrId, getAllItems, versionId, publishedVersion } = args;
    const params: GetAllDsrItemsRequest = {
      value: {
        requiresCounts: true,
        ...(whereClause ? { where: whereClause } : {}),
        ...(skip ? { skip } : {}),
        ...(take ? { take } : {}),
        params: { dsrId, getAllItems, versionId: versionId ?? undefined, publishedVersion },
      },
    };
    return await getCostDatabaseItems(params);
  }
  return undefined;
};

export const getEstimateItems = async <T extends Exact<GetEstimateItemsRequest, T>>(
  data: T,
): Promise<{ data: GetEstimateItemsResponse }> => {
  const response = await axiosWrapper.post<GetEstimateItemsResponse>(
    `${getBackendUrl()}/estimate/getEstimateItems`,
    data,
  );
  return response;
};

export const getCostDatabaseItems = async <T extends Exact<GetAllDsrItemsRequest, T>>(
  data: T,
): Promise<{ data: GetAllDsrItemsResponse }> => {
  const responseData = await axiosWrapper.post<GetAllDsrItemsResponse>(`${getBackendUrl()}/dsr/getAllDsrItems`, data);
  return responseData;
};

// Cost-database item WRITES now hit dedicated routes (split out of the read-only /getAllDsrItems).
export const insertCostDatabaseItem = async <T extends Exact<InsertDsrItemRequest, T>>(
  data: T,
): Promise<{ data: GetAllDsrItemsResponse }> => {
  return await axiosWrapper.post<GetAllDsrItemsResponse>(`${getBackendUrl()}/dsr/insertDsrItem`, data);
};

export const updateCostDatabaseItem = async <T extends Exact<UpdateDsrItemRequest, T>>(
  data: T,
): Promise<{ data: GetAllDsrItemsResponse }> => {
  return await axiosWrapper.post<GetAllDsrItemsResponse>(`${getBackendUrl()}/dsr/updateDsrItem`, data);
};

export const deleteCostDatabaseItems = async <T extends Exact<DeleteDsrItemRequest, T>>(
  data: T,
): Promise<{ data: GetAllDsrItemsResponse }> => {
  return await axiosWrapper.post<GetAllDsrItemsResponse>(`${getBackendUrl()}/dsr/deleteDsrItem`, data);
};

export const updateTakeoff = async ({
  takeOffItemId,
  name,
  description,
  additionalQuantity,
  estimateId,
  units,
  factor,
  lineColor,
  lineThickness,
  fillColor,
  fillOpacity,
  symbolSize,
  countSymbol,
}: {
  takeOffItemId: string;
  name?: string;
  description?: string;
  additionalQuantity?: string | number;
  estimateId: string;
  units?: string;
  factor?: number;
  lineColor?: string;
  lineThickness?: string | number;
  fillColor?: string;
  fillOpacity?: string | number;
  symbolSize?: string | number;
  countSymbol?: string | number;
}): Promise<{ data: GetTakeoffsListResponse }> => {
  const computedAdditionalQuantity = getDefaultQuantity({
    factor,
    quantity: Number.parseFloat(String(additionalQuantity)),
  });
  const data: GetTakeoffsListRequest = {
    value: {
      takeOffItemId,
      name,
      description,
      additionalQuantity: computedAdditionalQuantity,
      unitsId: units,
      lineColor,
      lineThickness: Number.parseFloat(String(lineThickness)),
      fillColor,
      fillOpacity: Number.parseFloat(String(fillOpacity)),
      symbolSize: Number.parseFloat(String(symbolSize)),
      countSymbol: Number.parseFloat(String(countSymbol)),
    },
    params: { estimateId },
  };
  const response = await axiosWrapper.post<GetTakeoffsListResponse>(`${getBackendUrl()}/takeoffs/updateTakeoff`, data);
  return response;
};

export const deleteTakeoff = async ({
  takeOffItemId,
  estimateId,
}: {
  takeOffItemId: string[];
  estimateId: string;
}): Promise<{ data: GetTakeoffsListResponse }> => {
  const data: GetTakeoffsListRequest = { key: takeOffItemId, params: { estimateId } };
  const response = await axiosWrapper.post<GetTakeoffsListResponse>(`${getBackendUrl()}/takeoffs/deleteTakeoff`, data);
  return response;
};

export const duplicateTakeoff = async ({
  takeOffItemId,
  name,
  description,
  type,
  linkMeasurements,
  linkItems,
  estimateId,
}: DuplicateTakeoffRequest): Promise<{ data: DuplicateTakeoffResponse }> => {
  const response = await axiosWrapper.post<DuplicateTakeoffResponse>(`${getBackendUrl()}/takeoffs/duplicateTakeoff`, {
    takeOffItemId,
    estimateId,
    name,
    description,
    type,
    linkMeasurements,
    linkItems,
  });
  return response;
};

export const duplicateItemInTakeoff = async ({
  itemId,
  takeOffItemId,
  estimateId,
}: DuplicateItemInTakeoffRequest): Promise<{ data: DuplicateItemInTakeoffResponse }> => {
  const response = await axiosWrapper.post<DuplicateItemInTakeoffResponse>(
    `${getBackendUrl()}/takeoffs/duplicateItemInTakeoff`,
    { itemId, takeOffItemId, estimateId },
  );
  return response;
};

export const addItemsToTakeoffs = async <T extends Exact<AddItemsToTakeoffsRequest, T>>(
  data: T,
  shouldReturnResult = true,
): Promise<{ data: AddItemsToTakeoffsResponse } | undefined> => {
  const response = await axiosWrapper.post<AddItemsToTakeoffsResponse>(
    `${getBackendUrl()}/takeoffs/addItemsToTakeoffs`,
    data,
  );
  if (shouldReturnResult) {
    return response;
  }
};

export const addMeasurementsToTakeoffs = async <T extends Exact<AddMeasurementsToTakeoffsRequest, T>>(
  data: T,
  shouldReturnResult = true,
): Promise<{ data: AddMeasurementsToTakeoffsResponse } | undefined> => {
  const response = await axiosWrapper.post<AddMeasurementsToTakeoffsResponse>(
    `${getBackendUrl()}/measurements/addMeasurementsToTakeoffs`,
    data,
  );
  if (shouldReturnResult) {
    return response;
  }
};

export const removeMeasurementsFromTakeoffs = async <T extends Exact<RemoveMeasurementsFromTakeoffsRequest, T>>(
  data: T,
  shouldReturnResult = true,
): Promise<{ data: RemoveMeasurementsFromTakeoffsResponse } | undefined> => {
  const response = await axiosWrapper.post<RemoveMeasurementsFromTakeoffsResponse>(
    `${getBackendUrl()}/measurements/removeMeasurementsFromTakeoffs`,
    data,
  );
  if (shouldReturnResult) return response;
};

export function setMeasurementsModalStatus({ status }: { status: boolean }) {
  if (status !== true && status !== false) return;
  return async (dispatch: AppDispatch) => dispatch(setMeasurementsModalStatusReducer({ status }));
}

export const addMeasurementsToTakeoffsFromNodes = async (data: AddMeasurementsToTakeoffsFromNodesRequest) =>
  await axiosWrapper.post<AddMeasurementsToTakeoffsFromNodesResponse>(
    `${getBackendUrl()}/measurements/addMeasurementsToTakeoffsFromNodes`,
    data,
  );

export const createMeasurement = async <T extends Exact<CreateMeasurementRequest, T>>(
  data: T,
): Promise<{ data: CreateMeasurementResponse }> => {
  const response = await axiosWrapper.post<CreateMeasurementResponse>(
    `${getBackendUrl()}/measurements/createMeasurement`,
    data,
  );
  return response;
};

export const updateMeasurement = async (
  params: GetMeasurementsRequest["value"],
): Promise<{ data: GetMeasurementsResponse }> => {
  const data: GetMeasurementsRequest = {
    value: params,
  };
  const response = await axiosWrapper.post<GetMeasurementsResponse>(
    `${getBackendUrl()}/measurements/updateMeasurement`,
    data,
  );
  return response;
};

export const duplicateMeasurement = async ({
  measurementId,
  estimateId,
  linkTakeoffs,
}: DuplicateMeasurementRequest): Promise<{ data: DuplicateMeasurementResponse }> => {
  const data: DuplicateMeasurementRequest = {
    measurementId,
    estimateId,
    linkTakeoffs,
  };
  const response = await axiosWrapper.post<DuplicateMeasurementResponse>(
    `${getBackendUrl()}/measurements/duplicateMeasurement`,
    data,
  );
  return response;
};

export const updateMeasurementTags = async ({
  measurementId,
  tags,
  estimateId,
}: UpdateMeasurementTagsRequest): Promise<{ data: UpdateMeasurementTagsResponse }> => {
  const data: UpdateMeasurementTagsRequest = { measurementId, tags, estimateId };
  const response = await axiosWrapper.post<UpdateMeasurementTagsResponse>(
    `${getBackendUrl()}/measurements/updateMeasurementTags`,
    data,
  );
  return response;
};

export function getMeasurements({
  estimateId,
  sheetId,
  takeOffItemId,
  fileVersionId,
  versionId,
}: {
  estimateId: string;
  sheetId?: number;
  takeOffItemId?: string;
  fileVersionId?: string;
  versionId?: string;
}) {
  return async (dispatch: AppDispatch) => {
    const data: GetMeasurementsRequest = {
      value: {
        params: {
          estimateId,
          sheetId,
          takeOffItemId,
          fileVersionId,
          versionId,
        },
      },
    };
    const response = await axiosWrapper.post<GetMeasurementsResponse>(
      `${getBackendUrl()}/measurements/getMeasurements`,
      data,
    );
    if (response.data.status) {
      dispatch(setMeasurementsData(response.data.result));
    }
  };
}

export function setMeasurements(data: MeasurementItem[] | undefined) {
  return (dispatch: AppDispatch) => {
    dispatch(setMeasurementsData(data));
  };
}

export const deleteMeasurement = async ({
  key,
  estimateId,
}: {
  key: string | string[];
  estimateId: string;
}): Promise<{ data: GetMeasurementsResponse }> => {
  const data: GetMeasurementsRequest = { key, params: { estimateId } };
  const response = await axiosWrapper.post<GetMeasurementsResponse>(
    `${getBackendUrl()}/measurements/deleteMeasurement`,
    data,
  );
  return response;
};

export async function getAdditionalCost({
  estimateId,
  versionId,
}: GetEstimateDataProp): Promise<{ data: GetAdditionalCostResponse }> {
  const data: GetAdditionalCostRequest = { value: { estimateId, versionId } };
  return await axiosWrapper.post<GetAdditionalCostResponse>(`${getBackendUrl()}/estimate/getAdditionalCost`, data);
}

export async function deleteAdditionalCost({
  uniqueIds,
  estimateId,
}: DeleteAdditionalCostRequest): Promise<{ data: GetAdditionalCostResponse }> {
  const data: DeleteAdditionalCostRequest = { estimateId, uniqueIds };
  const response = await axiosWrapper.post<GetAdditionalCostResponse>(
    `${getBackendUrl()}/estimate/deleteAdditionalCost`,
    data,
  );
  return response;
}

export const saveAdditionalCost = async ({
  estimateId,
  name,
  value,
  uniqueId,
  references,
  formula,
}: SaveAdditionalCostRequest): Promise<{ data: SaveAdditionalCostResponse }> => {
  const data: SaveAdditionalCostRequest = {
    estimateId,
    name,
    value,
    uniqueId,
    references,
    formula,
  };
  const response = await axiosWrapper.post<SaveAdditionalCostResponse>(
    `${getBackendUrl()}/estimate/saveAdditionalCost`,
    data,
  );
  return response;
};

export const createQuery = async ({
  fileVersionId,
  estimateId,
  query = "{}",
  name,
}: {
  fileVersionId: string;
  estimateId?: string;
  query?: string;
  name?: string;
}): Promise<{ data: QueriesDataResponse }> => {
  const data: QueriesDataRequest = {
    value: { fileVersionId, estimateId, query, name },
    action: "insert",
  };
  const response = await axiosWrapper.post<QueriesDataResponse>(`${getBackendUrl()}/queries/insertQuery`, data);
  return response;
};

export const getQueryData = async ({
  fileVersionId,
  id,
}: {
  fileVersionId: string;
  id?: string;
}): Promise<{ data: QueriesDataResponse }> => {
  const data: QueriesDataRequest = { value: { params: { fileVersionId, id } } };
  const response = await axiosWrapper.post<QueriesDataResponse>(`${getBackendUrl()}/queries/queriesData`, data);
  return response;
};

export const updateQuery = async ({
  id,
  name,
  queryRule,
}: {
  id: string;
  name?: string;
  queryRule?: string;
}): Promise<{ data: QueriesDataResponse }> => {
  const value: NonNullable<QueriesDataRequest["value"]> = { id };
  if (name) value.name = name;
  if (queryRule) value.queryRule = queryRule;

  const data: QueriesDataRequest = { action: "update", value };
  const response = await axiosWrapper.post<QueriesDataResponse>(`${getBackendUrl()}/queries/updateQuery`, data);
  return response;
};

export const deleteQueries = async ({ ids }: { ids: string[] }): Promise<{ data: QueriesDataResponse }> => {
  const data: QueriesDataRequest = { action: "remove", value: { ids } };
  const response = await axiosWrapper.post<QueriesDataResponse>(`${getBackendUrl()}/queries/removeQuery`, data);
  return response;
};

export const saveCurrentColumnSequence = async ({
  queryId,
  columns,
}: {
  queryId: string;
  columns?: string[];
}): Promise<{ data: SaveCurrentColumnSequenceResponse }> => {
  const data: SaveCurrentColumnSequenceRequest = {
    queryId,
    columns: columns ? JSON.stringify(columns) : undefined,
  };
  const response = await axiosWrapper.post<SaveCurrentColumnSequenceResponse>(
    `${getBackendUrl()}/queries/saveCurrentColumnSequence`,
    data,
  );
  return response;
};

export const getQuerySavedColumns = async <T extends Exact<GetQuerySavedColumnsRequest, T>>(
  data: T,
): Promise<{ data: GetQuerySavedColumnsResponse }> => {
  const response = await axiosWrapper.post<GetQuerySavedColumnsResponse>(
    `${getBackendUrl()}/queries/getQuerySavedColumns`,
    data,
  );
  return response;
};

export const saveItemVariableRate = async <T extends Exact<SaveItemVariableRateRequest, T>>(
  data: T,
): Promise<{ data: SaveItemVariableRateResponse }> => {
  const response = await axiosWrapper.post<SaveItemVariableRateResponse>(
    `${getBackendUrl()}/dsr/saveItemVariableRate`,
    data,
  );
  return response;
};

export const saveItemRangeUnitValue = async ({
  itemId,
  estimateId,
  rangeUnitValue,
}: {
  itemId: string;
  estimateId: string;
  rangeUnitValue: number;
}): Promise<{ data: GetEstimateItemsResponse }> => {
  const data: UpdateEstimateItemRequest = {
    value: { itemId, rangeUnitValue },
    params: { estimateId },
  };
  const response = await axiosWrapper.post<GetEstimateItemsResponse>(
    `${getBackendUrl()}/estimate/updateEstimateItem`,
    data,
  );
  return response;
};

export const saveItemAdditionalAmount = async ({
  itemId,
  additionalAmount,
  estimateId,
}: {
  itemId: string;
  additionalAmount: number;
  estimateId: string;
}): Promise<{ data: GetEstimateItemsResponse }> => {
  const data: UpdateEstimateItemRequest = {
    value: { itemId, additionalAmount },
    params: { estimateId },
  };
  const response = await axiosWrapper.post<GetEstimateItemsResponse>(
    `${getBackendUrl()}/estimate/updateEstimateItem`,
    data,
  );
  return response;
};

export const saveItemDetails = async <T extends Exact<SaveItemDetailsRequest, T>>(
  data: T,
): Promise<{ data: SaveItemDetailsResponse }> => {
  const response = await axiosWrapper.post<SaveItemDetailsResponse>(
    `${getBackendUrl()}/estimate/saveItemDetails`,
    data,
  );
  return response;
};

export const saveItemCostIndexValue = async ({
  itemId,
  estimateId,
  costIndex,
}: {
  itemId: string;
  estimateId: string;
  costIndex: number;
}): Promise<{ data: GetEstimateItemsResponse }> => {
  const data: UpdateEstimateItemRequest = {
    value: { itemId, costIndex },
    params: { estimateId },
  };
  const response = await axiosWrapper.post<GetEstimateItemsResponse>(
    `${getBackendUrl()}/estimate/updateEstimateItem`,
    data,
  );
  return response;
};

export const getEstimateVersionInfo = async ({
  estimateId,
}: GetEstimateVersionRequest): Promise<{ data: GetEstimateVersionResponse }> => {
  const response = await axiosWrapper.post<GetEstimateVersionResponse>(
    `${getBackendUrl()}/estimate/getEstimateVersion`,
    { estimateId },
  );
  return response;
};

export const createEstimateVersion = async ({
  estimateId,
}: CreateEstimateVersionRequest): Promise<{ data: CreateEstimateVersionResponse }> => {
  const response = await axiosWrapper.post<CreateEstimateVersionResponse>(
    `${getBackendUrl()}/estimate/createEstimateVersion`,
    { estimateId },
  );
  return response;
};

export const restoreEstimateVersion = async ({
  estimateId,
  restoreEstimateVersionId,
}: RestoreEstimateVersionRequest): Promise<{ data: RestoreEstimateVersionResponse }> => {
  const response = await axiosWrapper.post<RestoreEstimateVersionResponse>(
    `${getBackendUrl()}/estimate/restoreEstimateVersion`,
    { estimateId, restoreEstimateVersionId },
  );
  return response;
};

export const saveEstimateItemsFromExcelSheet = async <T extends Exact<SaveEstimateItemsFromExcelSheetRequest, T>>(
  data: T,
): Promise<{ data: SaveEstimateItemsFromExcelSheetResponse }> => {
  const response = await axiosWrapper.post<SaveEstimateItemsFromExcelSheetResponse>(
    `${getBackendUrl()}/estimate/saveEstimateItemsFromExcelSheet`,
    data,
  );
  return response;
};

export const importCostDatabaseItemsFromExcelSheet = async <
  T extends Exact<SaveCostDatabaseItemsFromExcelSheetRequest, T>,
>(
  data: T,
): Promise<{ data: SaveCostDatabaseItemsFromExcelSheetResponse }> => {
  const response = await axiosWrapper.post<SaveCostDatabaseItemsFromExcelSheetResponse>(
    `${getBackendUrl()}/dsr/saveCostDatabaseItemsFromExcelSheet`,
    data,
  );
  return response;
};

export const changeDsrVersionAttachedToEstimate = async ({
  _id,
  upgradeDsrVersion = true,
  forceDeletedItemToManual,
}: {
  _id: string;
  upgradeDsrVersion?: boolean;
  forceDeletedItemToManual?: boolean;
}): Promise<{ data: GetEstimateListResponse }> => {
  const data: UpdateEstimateDataRequest = { _id, upgradeDsrVersion, forceDeletedItemToManual };
  const response = await axiosWrapper.post<GetEstimateListResponse>(
    `${getBackendUrl()}/estimate/updateEstimateData`,
    data,
  );
  return response;
};

export const getAllVersionsOfDsr = async <T extends Exact<GetAllVersionsOfDsrRequest, T>>(
  data: T,
): Promise<{ data: GetAllVersionsOfDsrResponse }> => {
  const response = await axiosWrapper.post<GetAllVersionsOfDsrResponse>(
    `${getBackendUrl()}/dsr/getAllVersionsOfDsr`,
    data,
  );
  return response;
};

export const getCostDatabase =
  ({ _id, dsrVersionId }: { _id: string; dsrVersionId?: string }) =>
  async (dispatch: AppDispatch) => {
    const data: GetDsrDataRequest = { value: { _id, dsrVersionId } };
    const response = await axiosWrapper.post<GetDsrDataResponse>(`${getBackendUrl()}/dsr/getDsrData`, data);
    if (response.data.status && response.data.result) {
      dispatch(setCostdatabaseAction(response.data.result[0]));
    }
  };

export const getCostDatabaseById = async ({
  _id,
  dsrVersionId,
}: {
  _id: string;
  dsrVersionId?: string;
}): Promise<{ data: GetDsrDataResponse }> => {
  const data: GetDsrDataRequest = { value: { _id, dsrVersionId } };
  const response = await axiosWrapper.post<GetDsrDataResponse>(`${getBackendUrl()}/dsr/getDsrData`, data);
  return response;
};

export const clearCostDatabaseRedux = () => (dispatch: AppDispatch) => {
  dispatch(setCostdatabaseAction({}));
};

export const publishCostDatabaseVersion = async ({
  dsrId,
}: {
  dsrId: string;
}): Promise<{ data: PublishCostDatabaseVersionResponse }> => {
  const data: PublishCostDatabaseVersionRequest = { dsrId };
  const response = await axiosWrapper.post<PublishCostDatabaseVersionResponse>(
    `${getBackendUrl()}/dsr/publishCostDatabaseVersion`,
    data,
  );
  return response;
};

export const discardCostDatabaseDraftVersion = async ({
  dsrId,
}: {
  dsrId: string;
}): Promise<{ data: DiscardDraftVersionResponse }> => {
  const data: DiscardDraftVersionRequest = { dsrId };
  const response = await axiosWrapper.post<DiscardDraftVersionResponse>(
    `${getBackendUrl()}/dsr/discardDraftVersion`,
    data,
  );
  return response;
};

export const getEstimateTotal = async ({
  estimateId,
  versionId,
}: GetEstimateTotalRequest): Promise<{ data: GetEstimateTotalResponse }> => {
  const data: GetEstimateTotalRequest = { estimateId, versionId };
  const response = await axiosWrapper.post<GetEstimateTotalResponse>(
    `${getBackendUrl()}/estimate/getEstimateTotal`,
    data,
  );
  return response;
};

export function getEstimateAdditionalCostTotal({ estimateId, versionId }: GetEstimateAdditionalCostTotalRequest) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosWrapper.post<GetEstimateAdditionalCostTotalResponse>(
      `${getBackendUrl()}/estimate/getEstimateAdditionalCostTotal`,
      { estimateId, versionId },
    );

    if (response.data.status && response.data.data !== undefined) {
      dispatch(setAdditionalCostTotalAction(response.data.data));
    }
  };
}

export const updatedEstimateItemSerial = async <T extends Exact<UpdatedEstimateItemSerialRequest, T>>(
  data: T,
): Promise<{ data: UpdatedEstimateItemSerialResponse }> => {
  const response = await axiosWrapper.post<UpdatedEstimateItemSerialResponse>(
    `${getBackendUrl()}/estimate/updatedEstimateItemSerial`,
    data,
  );
  return response;
};

export const getPurchaseUnits = async (
  data: GetPurchaseUnitsRequest | null,
): Promise<{ data: GetPurchaseUnitsResponse }> => {
  const response = await axiosWrapper.post<GetPurchaseUnitsResponse>(
    `${getBackendUrl()}/units/getPurchaseUnits`,
    data ?? {},
  );
  return response;
};

// The former /getMeasurementUnits & /getPurchaseUnits action-dispatchers were split into discrete REST
// routes; these wrappers pick the endpoint from `action` and send the exact shape each handler reads:
// create/update take the flat `value` object, remove takes `{ key }`. Reads stay on getMeasurementUnits /
// getPurchaseUnits (the repurposed pure-read routes).
const MEASUREMENT_UNIT_ACTION_PATHS: Record<string, string> = {
  insert: "/units/createMeasurementUnit",
  update: "/units/updateMeasurementUnit",
  remove: "/units/deleteMeasurementUnit",
};
export const saveMeasurementUnit = async (
  data: GetMeasurementUnitsRequest,
): Promise<{ data: GetMeasurementUnitsResponse }> => {
  const action = data.action ?? "insert";
  const path = MEASUREMENT_UNIT_ACTION_PATHS[action] ?? MEASUREMENT_UNIT_ACTION_PATHS.insert;
  const body = action === "remove" ? { key: data.key } : (data.value ?? {});
  return axiosWrapper.post<GetMeasurementUnitsResponse>(`${getBackendUrl()}${path}`, body);
};

const PURCHASE_UNIT_ACTION_PATHS: Record<string, string> = {
  insert: "/units/createPurchaseUnit",
  update: "/units/updatePurchaseUnit",
  remove: "/units/deletePurchaseUnit",
};
export const savePurchaseUnit = async (data: GetPurchaseUnitsRequest): Promise<{ data: GetPurchaseUnitsResponse }> => {
  const action = data.action ?? "insert";
  const path = PURCHASE_UNIT_ACTION_PATHS[action] ?? PURCHASE_UNIT_ACTION_PATHS.insert;
  const body = action === "remove" ? { key: data.key } : (data.value ?? {});
  return axiosWrapper.post<GetPurchaseUnitsResponse>(`${getBackendUrl()}${path}`, body);
};

export const checkIfDsrVersionIsLatest = async ({
  estimateId,
}: CheckIfDsrVersionIsLatestRequest): Promise<{ data: CheckIfDsrVersionIsLatestResponse }> => {
  const response = await axiosWrapper.post<CheckIfDsrVersionIsLatestResponse>(
    `${getBackendUrl()}/estimate/checkIfDsrVersionIsLatest`,
    { estimateId },
  );
  return response;
};

export const fetchTakeoffsListAttachedToItem = async ({
  itemId,
  estimateId,
  versionId,
}: GetTakeoffsListAttachedToItemRequest): Promise<{ data: GetTakeoffsListAttachedToItemResponse }> => {
  const response = await axiosWrapper.post<GetTakeoffsListAttachedToItemResponse>(
    `${getBackendUrl()}/takeoffs/getTakeoffsListAttachedToItem`,
    { itemId, estimateId, versionId },
  );
  return response;
};

// Dispatches a BOQ item insert/update/delete to its dedicated REST endpoint (the former
// /getEstimateItems action-dispatcher has been split). `action` selects the endpoint and is
// stripped from the request body; each endpoint validates its own dedicated schema.
const ESTIMATE_ITEM_ACTION_PATHS: Record<string, string> = {
  insert: "/estimate/insertEstimateItem",
  update: "/estimate/updateEstimateItem",
  remove: "/estimate/deleteEstimateItem",
};
export const saveItemInEstimate = async (
  data: SaveItemInEstimatePayload,
): Promise<{ data: GetEstimateItemsResponse }> => {
  const { action, ...body } = data;
  const path = ESTIMATE_ITEM_ACTION_PATHS[action] || "/estimate/deleteEstimateItem";
  const response = await axiosWrapper.post<GetEstimateItemsResponse>(`${getBackendUrl()}${path}`, body);
  return response;
};

export const rateAnalysisItemType = {
  COST_DATABASE: "Cost Database Item",
  MANUAL: "Manual Item",
  ADDITIONAL_CHARGE: "Additional Charge",
  TOTAL: "Total",
  SUB_TOTAL: "Sub Total",
  MARKETRATE: "Market Rate Item",
};

export const changeItemTypeInEstimate = async ({
  itemId,
  estimateId,
}: ChangeItemTypeInEstimateRequest): Promise<{ data: ChangeItemTypeInEstimateResponse }> => {
  const response = await axiosWrapper.post<ChangeItemTypeInEstimateResponse>(
    `${getBackendUrl()}/estimate/changeItemTypeInEstimate`,
    { itemId, estimateId },
  );
  return response;
};

export const upgradeTakeOffAttachedQuery = async <T extends Exact<UpgradeTakeOffAttachedQueryRequest, T>>(
  data: T,
): Promise<{ data: UpgradeTakeOffAttachedQueryResponse }> => {
  const response = await axiosWrapper.post<UpgradeTakeOffAttachedQueryResponse>(
    `${getBackendUrl()}/takeoffs/upgradeTakeOffAttachedQuery`,
    data,
  );
  return response;
};

export const removeTakeOffAttachedQuery = async <T extends Exact<RemoveTakeOffAttachedQueryRequest, T>>(
  data: T,
): Promise<{ data: RemoveTakeOffAttachedQueryResponse }> => {
  const response = await axiosWrapper.post<RemoveTakeOffAttachedQueryResponse>(
    `${getBackendUrl()}/takeoffs/removeTakeOffAttachedQuery`,
    data,
  );
  return response;
};

export const getEstimateItemsRemoteData = async ({
  skip,
  take,
  params,
}: GetEstimateItemsRemoteRequest): Promise<{ data: GetEstimateItemsRemoteResponse }> => {
  const response = await axiosWrapper.post<GetEstimateItemsRemoteResponse>(
    `${getBackendUrl()}/estimate/getEstimateItemsRemote`,
    { skip, take, params },
  );
  return response;
};

export const saveFileCalibrationToEstimate = async ({
  fileVersionId,
  scale,
}: {
  fileVersionId?: string;
  scale: number;
}): Promise<{ data: SaveUserSettingsResponse }> => {
  if (!fileVersionId) return { data: { status: false, errorMessage: "Missing fileVersionId" } };
  const data: SaveUserSettingsRequest = {
    resourceId: fileVersionId,
    data: scale,
    type: userSettingsType.FILE_CALIBRATION,
  };
  const response = await axiosWrapper.post<SaveUserSettingsResponse>(
    `${getBackendUrl()}/userSettings/saveUserSettings`,
    data,
  );
  return response;
};

export const updateEstimateAttachedCostDatabase = async ({
  estimateId,
  dsrId,
}: UpdateCostDatabaseInEstimateRequest): Promise<{ data: UpdateCostDatabaseInEstimateResponse }> => {
  const response = await axiosWrapper.post<UpdateCostDatabaseInEstimateResponse>(
    `${getBackendUrl()}/estimate/updateCostDatbaseInEstimate`,
    { estimateId, dsrId },
  );
  return response;
};

export const updateEstimateData = async ({ estimateId, costIndex, name, description }: UpdateEstimateDataArgs) => {
  const data: UpdateEstimateDataRequest = { _id: estimateId, costIndex, name, description };
  const response = await axiosWrapper.post<GetEstimateListResponse>(
    `${getBackendUrl()}/estimate/updateEstimateData`,
    data,
  );
  return response;
};

export const getMeasurementUnits = async <T extends Exact<GetMeasurementUnitsRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<GetMeasurementUnitsResponse>(portalMeasurementUnitsUrl, data);
  return response;
};

export const getMeasurementsList = async <T extends Exact<GetMeasurementsRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<GetMeasurementsResponse>(estimateMeasurementsUrl, data);
  return response;
};

export const saveEstimateGeneralSettings = async <T extends Exact<saveEstimateSettingsParams, T>>(
  data: T,
): Promise<{ data: StatusResponse }> => {
  const response = await axiosWrapper.post<StatusResponse>(`${getBackendUrl()}/estimate/saveEstimateSettings`, data);
  return response;
};

export const getEstimateNumberValueBasedOnSettings = (
  value: number | string,
  estimateData: EstimateData | EstimateListItem,
  decimalField: keyof NonNullable<NonNullable<EstimateData["settings"]>["decimalPlaces"]> = "quantity",
) => {
  const { decimalPlaces, digitGrouping, largeNumber } = estimateData.settings!;
  return getNumberBasedOnSettings({
    value: Number(value),
    decimalPlaces: decimalPlaces![decimalField]!,
    digitGrouping: digitGrouping!,
    largeNumber: largeNumber!,
  });
};

export const getTakeoffWizard = async <T extends Exact<GetTakeoffWizardRequest, T>>(
  data: T,
): Promise<{ data: GetTakeoffWizardResponse }> => {
  const response = await axiosWrapper.post<GetTakeoffWizardResponse>(
    `${getBackendUrl()}/takeoffs/getTakeoffWizard`,
    data,
  );
  return response;
};

// The former /getTakeoffWizard action-dispatcher (save/remove/read) was split; getTakeoffWizard above is
// now the pure READ. These two wrappers hit the discrete write/delete routes (the handlers spread the whole
// body, so send the fields directly).
export const saveTakeoffWizard = async (data: {
  estimateId: string;
  fileVersionId: string;
  value: Record<string, unknown>;
}): Promise<{ data: GetTakeoffWizardResponse }> => {
  return axiosWrapper.post<GetTakeoffWizardResponse>(`${getBackendUrl()}/takeoffs/saveTakeoffWizard`, data);
};

export const removeTakeoffWizard = async (data: {
  estimateId: string;
  fileVersionId: string;
  wizardId: string | undefined;
}): Promise<{ data: GetTakeoffWizardResponse }> => {
  return axiosWrapper.post<GetTakeoffWizardResponse>(`${getBackendUrl()}/takeoffs/deleteTakeoffWizard`, data);
};

export const getTakeoffWizardResults = async <T extends Exact<GetTakeoffWizardMeasurementsRequest, T>>(
  data: T,
): Promise<{ data: GetTakeoffWizardMeasurementsResponse }> => {
  const response = await axiosWrapper.post<GetTakeoffWizardMeasurementsResponse>(
    `${getBackendUrl()}/takeoffs/getTakeoffWizardMeasurements`,
    data,
  );
  return response;
};

export const isEstimateRequiredPermission = (
  estimateData: EstimateData | EstimateListItem,
  requiredPermissionId: number,
): boolean => {
  return _.get(estimateData, "userPermissions") ? estimateData.userPermissions!.includes(requiredPermissionId) : false;
};

export const getEstimateValidItems = async (data: GetValidItemsRequest): Promise<{ data: GetValidItemsResponse }> => {
  const response = await axiosWrapper.post<GetValidItemsResponse>(getValidItemsUrl, data);
  return response;
};
